/**
 * Servicio de Transacciones
 * Maneja envío y recepción de tokens nativos y ERC-20
 */

import { ethers } from 'ethers';
import config from '@/config';
import providerService from './provider.service';
import balanceService from './balance.service';
import WalletManager from '../wallet/wallet-manager';
import { TransactionSyncService } from '../transactions/transaction-sync.service';

interface SendTransactionParams {
  to: string;
  amount: string;
  tokenAddress?: string;
  gasLimit?: string;
  gasPrice?: string;
}

interface TransactionResult {
  hash: string;
  blockNumber?: number;
  from?: string;
  to: string;
  value: string;
  symbol?: string;
  tokenAddress?: string;
  confirmed: boolean;
}

// Anti-duplicados: Mapa de transferencias pendientes
// Key: hash(to_amount_token), Value: timestamp
const pendingTransfers = new Map<string, number>();
const DUPLICATE_COOLDOWN = 30 * 1000; // 30 segundos
const GLOBAL_COOLDOWN = 3 * 1000; // 3 segundos entre cualquier envío
let lastTransferTime = 0;

/**
 * Servicio de Transacciones
 */
export const transactionService = {
  /**
   * Envía tokens nativos (ETH, BNB, OMDB, etc.)
   */
  async sendNativeToken(
    to: string,
    amount: string,
    networkId: string,
    gasLimit?: string,
    gasPrice?: string
  ): Promise<TransactionResult> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        throw new Error(`No provider available for network ${networkId}`);
      }

      // Obtener wallet desde memoria
      const walletManager = WalletManager.getInstance();
      const wallet = walletManager.getActiveWallet();
      const signer = wallet.connect(provider);

      // Crear transacción
      const tx: any = {
        to,
        value: ethers.parseEther(amount),
      };

      // Añadir gas si se proporciona
      if (gasLimit) tx.gasLimit = BigInt(gasLimit);
      if (gasPrice) tx.gasPrice = BigInt(gasPrice);

      console.log('[Transaction] Enviando token nativo:', { to, amount });

      // Para OMDB, firmar en cliente pero enviar vía backend
      if (networkId === 'omdb' || networkId === 'omdb-local') {
        console.log('[Transaction] OMDB: Firmando en cliente y enviando vía backend...');

        try {
          // Obtener nonce del provider existente (usa proxy)
          const nonce = await provider.getTransactionCount(wallet.address);
          tx.nonce = nonce;

          // Obtener gasPrice con timeout y fallback
          if (!tx.gasPrice) {
            try {
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('getFeeData timeout')), 3000)
              );
              const feeData = await Promise.race([
                provider.getFeeData(),
                timeoutPromise
              ]) as ethers.FeeData;
              tx.gasPrice = feeData.gasPrice || ethers.parseUnits('10', 'gwei');
            } catch {
              console.warn('[Transaction] OMDB: getFeeData falló/timeout, usando gas price por defecto (10 gwei)');
              tx.gasPrice = ethers.parseUnits('10', 'gwei');
            }
          }

          // Estimar gas si no se proporciona
          if (!tx.gasLimit) {
            tx.gasLimit = BigInt(21000); // Gas mínimo para tx nativa
          }

          // Establecer chainId explícitamente (viene de VITE_OMDB_CHAIN_ID)
          tx.chainId = config.getNetwork('omdb').chainId;

          console.log('[Transaction] OMDB: Parámetros de tx:', {
            nonce: tx.nonce,
            gasPrice: tx.gasPrice?.toString(),
            gasLimit: tx.gasLimit?.toString(),
            chainId: tx.chainId
          });

          // Firmar transacción
          const signedTx = await signer.signTransaction(tx);

          // Enviar vía endpoint backend
          const response = await fetch(`${config.getApiBaseUrl()}/api/send-omdb-tx`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ signedTx })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to send transaction');
          }

          const { txHash } = await response.json();
          console.log('[Transaction] OMDB: Transacción enviada con hash:', txHash);

          // Guardar en cache inmediatamente
          const syncService = TransactionSyncService.getInstance();
          syncService.addUserTransaction(wallet.address, networkId, {
            hash: txHash,
            from: wallet.address,
            to,
            value: amount,
            timestamp: Date.now(),
            blockNumber: 0,
            status: 'pending',
            type: 'send',
            network: networkId,
            source: 'user_sent',
          });

          // Retornar inmediatamente sin esperar confirmación
          console.log('[Transaction] OMDB: Transacción guardada como pending');
          return {
            hash: txHash,
            from: wallet.address,
            to,
            value: amount,
            confirmed: false,
          };
        } catch (omdbError) {
          console.error('[Transaction] OMDB: Error:', omdbError);
          throw omdbError;
        }
      }

      // Para otras redes, enviar normalmente
      let transaction: ethers.ContractTransactionResponse | ethers.TransactionResponse;
      transaction = await signer.sendTransaction(tx);

      // Guardar transacción en cache inmediatamente
      const syncService = TransactionSyncService.getInstance();
      syncService.addUserTransaction(wallet.address, networkId, {
        hash: transaction.hash,
        from: wallet.address,
        to,
        value: amount,
        timestamp: Date.now(),
        blockNumber: 0,
        status: 'pending',
        type: 'send',
        network: networkId,
        source: 'user_sent',
      });

      // Esperar confirmación
      const receipt = await transaction.wait();

      if (!receipt) {
        throw new Error('Transaction receipt is null');
      }

      console.log('[Transaction] Transacción confirmada:', receipt.hash);

      return {
        hash: receipt.hash,
        blockNumber: receipt.blockNumber || undefined,
        from: receipt.from,
        to: receipt.to || to,
        value: amount,
        confirmed: true,
      };
    } catch (error) {
      console.error('Error enviando token nativo:', error);
      throw new Error('Failed to send native token: ' + (error as Error).message);
    }
  },

  /**
   * Envía tokens ERC-20
   */
  async sendERC20Token(
    to: string,
    amount: string,
    tokenAddress: string,
    networkId: string,
    gasLimit?: string,
    gasPrice?: string
  ): Promise<TransactionResult> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        throw new Error(`No provider available for network ${networkId}`);
      }

      // Obtener wallet desde memoria
      const walletManager = WalletManager.getInstance();
      const wallet = walletManager.getActiveWallet();
      const signer = wallet.connect(provider);

      // Crear contrato del token
      const tokenContract = new ethers.Contract(tokenAddress, config.erc20Abi, signer);

      // Obtener decimales del token
      const decimals = await tokenContract.decimals();
      const symbol = await tokenContract.symbol();

      // Convertir monto a unidades del token
      const tokenAmount = ethers.parseUnits(amount, decimals);

      console.log('[Transaction] Enviando token ERC-20:', { to, amount, tokenAddress });

      // Preparar opciones de transacción
      const txOptions: any = {};
      if (gasLimit) txOptions.gasLimit = BigInt(gasLimit);
      if (gasPrice) txOptions.gasPrice = BigInt(gasPrice);

      // Enviar transacción
      const transaction = await tokenContract.transfer(to, tokenAmount, txOptions);

      // Guardar transacción en cache inmediatamente
      const syncService = TransactionSyncService.getInstance();
      syncService.addUserTransaction(wallet.address, networkId, {
        hash: transaction.hash,
        from: wallet.address,
        to,
        value: amount,
        timestamp: Date.now(),
        blockNumber: 0, // Se actualizará cuando se confirme
        status: 'pending',
        type: 'send',
        network: networkId,
        tokenAddress,
        tokenSymbol: symbol,
        tokenDecimals: decimals,
        source: 'user_sent',
      });

      // Esperar confirmación
      const receipt = await transaction.wait();

      if (!receipt) {
        throw new Error('Transaction receipt is null');
      }

      console.log('[Transaction] Transacción ERC-20 confirmada:', receipt.hash);

      return {
        hash: receipt.hash,
        blockNumber: receipt.blockNumber || undefined,
        from: receipt.from,
        to,
        value: amount,
        symbol,
        tokenAddress,
        confirmed: true,
      };
    } catch (error) {
      console.error('Error enviando token ERC-20:', error);
      throw new Error('Failed to send ERC-20 token: ' + (error as Error).message);
    }
  },

  /**
   * Envía transacción (detecta automáticamente si es nativo o ERC-20)
   */
  async sendTransaction(
    params: SendTransactionParams,
    networkId: string
  ): Promise<TransactionResult> {
    const { to, amount, tokenAddress, gasLimit, gasPrice } = params;

    // 1. Validaciones previas
    const validation = this.validateSendParams(to, amount);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // 2. Anti-duplicados y Cooldown
    const now = Date.now();
    
    // Cooldown global
    if (now - lastTransferTime < GLOBAL_COOLDOWN) {
      throw new Error(`Por favor, espera unos segundos antes de realizar otra transferencia.`);
    }

    // Anti-duplicados específicos
    const transferKey = `${to.toLowerCase()}_${amount}_${(tokenAddress || 'native').toLowerCase()}`;
    const lastTime = pendingTransfers.get(transferKey);
    
    if (lastTime && now - lastTime < DUPLICATE_COOLDOWN) {
      throw new Error(`Ya hay una transferencia idéntica en proceso. Por favor, espera 30 segundos.`);
    }

    // 3. Verificación de saldo
    const balanceCheck = await this.checkSufficientBalance(amount, networkId, tokenAddress);
    if (!balanceCheck.sufficient) {
      throw new Error(balanceCheck.error);
    }

    // Registrar tiempo y transferencia pendiente
    lastTransferTime = now;
    pendingTransfers.set(transferKey, now);

    try {
      // Si no hay tokenAddress o es la dirección cero, es token nativo
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        return await this.sendNativeToken(to, amount, networkId, gasLimit, gasPrice);
      }

      // Si hay tokenAddress, es ERC-20
      return await this.sendERC20Token(to, amount, tokenAddress, networkId, gasLimit, gasPrice);
    } catch (error) {
      // Limpiar en caso de error inmediato
      pendingTransfers.delete(transferKey);
      throw error;
    } finally {
      // Limpiar después del cooldown (opcional, el Map se puede limpiar periódicamente)
      setTimeout(() => {
        pendingTransfers.delete(transferKey);
      }, DUPLICATE_COOLDOWN);
    }
  },

  /**
   * Valida los parámetros básicos de envío
   */
  validateSendParams(to: string, amount: string): { valid: boolean; error?: string } {
    if (!to || !ethers.isAddress(to)) {
      return { valid: false, error: 'La dirección de destino no es válida.' };
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return { valid: false, error: 'El monto debe ser un número mayor a cero.' };
    }

    return { valid: true };
  },

  /**
   * Verifica si el usuario tiene saldo suficiente (nativo para gas + token)
   */
  async checkSufficientBalance(
    amount: string,
    networkId: string,
    tokenAddress?: string
  ): Promise<{ sufficient: boolean; error?: string }> {
    try {
      const walletManager = WalletManager.getInstance();
      const address = walletManager.getActiveAddress();
      if (!address) throw new Error('Wallet bloqueada');

      const provider = providerService.getProvider(networkId);
      if (!provider) throw new Error('Provider no disponible');

      const network = config.getNetwork(networkId);
      
      // Estimar gas (aproximado)
      let gasEstimate: bigint;
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        gasEstimate = await this.estimateNativeGas(address, address, amount, networkId);
      } else {
        gasEstimate = await this.estimateERC20Gas(address, address, amount, tokenAddress, 18, networkId);
      }

      const gasPrice = await providerService.getGasPrice(networkId);
      const gasCost = gasEstimate * gasPrice;
      const gasCostEth = parseFloat(ethers.formatEther(gasCost));

      // 1. Verificar saldo nativo para GAS
      const nativeBalance = await balanceService.fetchNativeBalance(address, networkId, provider);
      
      if (nativeBalance < gasCostEth) {
        return { 
          sufficient: false, 
          error: `Saldo insuficiente para gas. Necesitas aprox. ${gasCostEth.toFixed(6)} ${network.nativeCurrency.symbol}.` 
        };
      }

      // 2. Verificar saldo del TOKEN
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        // Nativo
        if (nativeBalance < parseFloat(amount) + gasCostEth) {
          return { sufficient: false, error: 'Saldo insuficiente para cubrir el monto y el gas.' };
        }
      } else {
        // ERC20
        // Necesitamos decimals para el balance del token
        const tokenContract = new ethers.Contract(tokenAddress, ['function decimals() view returns (uint8)'], provider);
        const decimals = await tokenContract.decimals();
        const tokenBalanceStr = await balanceService.fetchTokenBalance(address, tokenAddress, decimals, provider);
        const tokenBalance = parseFloat(tokenBalanceStr);

        if (tokenBalance < parseFloat(amount)) {
          return { sufficient: false, error: 'Saldo insuficiente del token.' };
        }
      }

      return { sufficient: true };
    } catch (error) {
      console.error('[Transaction Service] Error verificando saldo:', error);
      // En caso de error, permitimos intentar la tx pero logueamos
      return { sufficient: true }; 
    }
  },

  /**
   * Obtiene el historial de transacciones desde el cache
   */
  getTransactionHistory(address: string, networkId: string): any[] {
    try {
      const syncService = TransactionSyncService.getInstance();
      return syncService.getTransactions(address, networkId);
    } catch (error) {
      console.error('Error obteniendo historial de transacciones:', error);
      return [];
    }
  },

  /**
   * Obtiene transacciones pendientes
   */
  getPendingTransactions(address: string, networkId: string): any[] {
    try {
      const syncService = TransactionSyncService.getInstance();
      return syncService.getPendingTransactions(address, networkId);
    } catch (error) {
      console.error('Error obteniendo transacciones pendientes:', error);
      return [];
    }
  },

  /**
   * Fuerza una sincronización de transacciones
   */
  async syncTransactions(address: string, networkId?: string): Promise<void> {
    try {
      const syncService = TransactionSyncService.getInstance();
      await syncService.forcSync(address, networkId);
    } catch (error) {
      console.error('Error sincronizando transacciones:', error);
    }
  },

  /**
   * Inicia la sincronización automática
   */
  startAutoSync(address: string): void {
    const syncService = TransactionSyncService.getInstance();
    syncService.startAutoSync(address);
  },

  /**
   * Detiene la sincronización automática
   */
  stopAutoSync(address: string): void {
    const syncService = TransactionSyncService.getInstance();
    syncService.stopAutoSync(address);
  },

  // Métodos existentes para compatibilidad
  async estimateNativeGas(
    from: string,
    to: string,
    amount: string,
    networkId: string
  ): Promise<bigint> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        return ethers.parseUnits('21000', 'wei');
      }

      const tx = {
        from,
        to,
        value: ethers.parseEther(amount),
      };

      const gasEstimate = await provider.estimateGas(tx);
      return gasEstimate;
    } catch (error) {
      console.error('Error estimando gas:', error);
      return ethers.parseUnits('21000', 'wei');
    }
  },

  async estimateERC20Gas(
    from: string,
    to: string,
    amount: string,
    tokenAddress: string,
    decimals: number,
    networkId: string
  ): Promise<bigint> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        return ethers.parseUnits('65000', 'wei');
      }

      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function transfer(address to, uint256 amount) returns (bool)'],
        provider
      );

      const tokenAmount = ethers.parseUnits(amount, decimals);
      const gasEstimate = await tokenContract.transfer.estimateGas(to, tokenAmount, { from });

      return gasEstimate;
    } catch (error) {
      console.error('Error estimando gas ERC-20:', error);
      return ethers.parseUnits('65000', 'wei');
    }
  },

  async getTransaction(txHash: string, networkId: string): Promise<any | null> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        return null;
      }

      const tx = await provider.getTransaction(txHash);
      return tx;
    } catch (error) {
      console.error('Error obteniendo transacción:', error);
      return null;
    }
  },

  async getTransactionReceipt(txHash: string, networkId: string): Promise<any | null> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        return null;
      }

      const receipt = await provider.getTransactionReceipt(txHash);
      return receipt;
    } catch (error) {
      console.error('Error obteniendo recibo de transacción:', error);
      return null;
    }
  },

  async waitForTransaction(
    txHash: string,
    networkId: string,
    confirmations = 1
  ): Promise<any | null> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider || providerService.isFallbackProvider(provider)) {
        return null;
      }

      const receipt = await provider.waitForTransaction(txHash, confirmations);
      return receipt;
    } catch (error) {
      console.error('Error esperando confirmación:', error);
      return null;
    }
  },
};

export default transactionService;
